<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import { authApi } from "../lib/api";
  import { authStore, addNotification } from "../lib/stores";
  import Toast from "../components/overlay/Toast.svelte";
  import Button from "../components/input/Button.svelte";

  let name = $state("");
  let email = $state("");
  let password = $state("");
  let passwordConfirm = $state("");
  let agreeTerms = $state(false);
  let loading = $state(false);
  let showPassword = $state(false);
  let errors = $state<Record<string, string>>({});

  function handleSocialLogin(provider: string) {
    addNotification("info", `${provider}로 가입은 현재 준비 중입니다. 곧 지원될 예정입니다!`);
  }

  function validateForm(): boolean {
    errors = {};

    if (name.length < 2) {
      errors.name = "이름은 2자 이상이어야 합니다.";
    }

    if (!email.includes("@")) {
      errors.email = "올바른 이메일 주소를 입력해주세요.";
    }

    if (password.length < 8) {
      errors.password = "비밀번호는 8자 이상이어야 합니다.";
    }

    if (password !== passwordConfirm) {
      errors.passwordConfirm = "비밀번호가 일치하지 않습니다.";
    }

    if (!agreeTerms) {
      errors.terms = "이용약관에 동의해주세요.";
    }

    return Object.keys(errors).length === 0;
  }

  async function handleSubmit() {
    if (!validateForm()) {
      return;
    }

    loading = true;

    try {
      const response = await authApi.signup(name, email, password);

      if (response.success) {
        localStorage.setItem("auth_token", response.data.tokens.access_token);
        authStore.login(response.data.user);
        addNotification("success", "회원가입이 완료되었습니다!");
        router.visit("/trips");
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach((err: any) => {
          if (err.field) {
            errors[err.field] = err.message;
          } else {
            addNotification("error", err.message);
          }
        });
        errors = { ...errors }; // Trigger reactivity
      } else {
        addNotification("error", "회원가입에 실패했습니다. 다시 시도해주세요.");
      }
    } finally {
      loading = false;
    }
  }
</script>

<div class="min-h-screen flex">
  <!-- Left side - Image/illustration -->
  <div class="hidden lg:flex lg:flex-1 bg-gradient-to-br from-secondary-500 to-primary-600 items-center justify-center p-12">
    <div class="max-w-lg text-white text-center">
      <div class="mb-8">
        <svg class="w-24 h-24 mx-auto opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 class="text-3xl font-bold mb-4">지금 바로 시작하세요</h2>
      <p class="text-lg opacity-90">
        무료로 가입하고 스마트한 여행 경비 관리를 시작하세요.
        AI 영수증 인식, 자동 정산까지 모든 기능을 무료로 이용할 수 있습니다.
      </p>

      <!-- Features list -->
      <div class="mt-8 text-left">
        <div class="flex items-center mb-4">
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          <span>무제한 여행 등록</span>
        </div>
        <div class="flex items-center mb-4">
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          <span>AI 영수증 인식</span>
        </div>
        <div class="flex items-center mb-4">
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          <span>자동 정산 계산</span>
        </div>
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          <span>다중 통화 지원</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Right side - Signup form -->
  <div class="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full">
      <div class="text-center mb-8">
        <Link href="/" class="inline-block text-3xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
          TripHelper
        </Link>
        <h2 class="mt-6 text-2xl font-semibold text-primary">회원가입</h2>
        <p class="mt-2 text-secondary">
          이미 계정이 있으신가요?
          <Link href="/login" class="text-primary-600 hover:text-primary-500 font-medium">
            로그인
          </Link>
        </p>
      </div>

      <div class="card">
        <form onsubmit={(e) => { e.preventDefault(); handleSubmit(); }} class="space-y-5">
          <div>
            <label for="name" class="label">이름</label>
            <input
              id="name"
              type="text"
              bind:value={name}
              class="input"
              class:input-error={errors.name}
              placeholder="홍길동"
              required
              autocomplete="name"
            />
            {#if errors.name}
              <p class="mt-1 text-sm text-error">{errors.name}</p>
            {/if}
          </div>

          <div>
            <label for="email" class="label">이메일</label>
            <input
              id="email"
              type="email"
              bind:value={email}
              class="input"
              class:input-error={errors.email}
              placeholder="you@example.com"
              required
              autocomplete="email"
            />
            {#if errors.email}
              <p class="mt-1 text-sm text-error">{errors.email}</p>
            {/if}
          </div>

          <div>
            <label for="password" class="label">비밀번호</label>
            <div class="relative">
              {#if showPassword}
                <input
                  id="password"
                  type="text"
                  bind:value={password}
                  class="input pr-10"
                  class:input-error={errors.password}
                  placeholder="••••••••"
                  required
                  autocomplete="new-password"
                />
              {:else}
                <input
                  id="password"
                  type="password"
                  bind:value={password}
                  class="input pr-10"
                  class:input-error={errors.password}
                  placeholder="••••••••"
                  required
                  autocomplete="new-password"
                />
              {/if}
              <button
                type="button"
                onclick={() => (showPassword = !showPassword)}
                class="absolute inset-y-0 right-0 flex items-center pr-3 text-muted hover:text-secondary"
              >
                {#if showPassword}
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                {:else}
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                {/if}
              </button>
            </div>
            {#if errors.password}
              <p class="mt-1 text-sm text-error">{errors.password}</p>
            {:else}
              <p class="mt-1 text-sm text-muted">8자 이상 입력해주세요.</p>
            {/if}
          </div>

          <div>
            <label for="passwordConfirm" class="label">비밀번호 확인</label>
            <input
              id="passwordConfirm"
              type="password"
              bind:value={passwordConfirm}
              class="input"
              class:input-error={errors.passwordConfirm}
              placeholder="••••••••"
              required
              autocomplete="new-password"
            />
            {#if errors.passwordConfirm}
              <p class="mt-1 text-sm text-error">{errors.passwordConfirm}</p>
            {/if}
          </div>

          <div class="flex items-start">
            <input
              type="checkbox"
              bind:checked={agreeTerms}
              class="mt-1 rounded text-primary-600 focus:ring-primary-500"
              id="terms"
            />
            <label for="terms" class="ml-2 text-sm text-secondary">
              <span class="text-primary-600 hover:underline cursor-pointer">이용약관</span> 및
              <span class="text-primary-600 hover:underline cursor-pointer">개인정보처리방침</span>에
              동의합니다.
            </label>
          </div>
          {#if errors.terms}
            <p class="text-sm text-error">{errors.terms}</p>
          {/if}

          <Button type="submit" variant="primary" fullWidth loading={loading}>회원가입</Button>
        </form>

        <!-- Social signup -->
        <div class="mt-6">
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-bright"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-surface text-muted">또는</span>
            </div>
          </div>

          <div class="mt-6 grid grid-cols-2 gap-3">
            <button
              type="button"
              onclick={() => handleSocialLogin("Google")}
              class="w-full flex items-center justify-center px-4 py-2 border border-bright rounded-lg shadow-sm text-sm font-medium text-secondary bg-surface hover:bg-surface-hover transition-colors"
            >
              <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#EA4335" d="M5.266 9.765A7.077 7.077 0 0 1 12 4.909c1.69 0 3.218.6 4.418 1.582L19.91 3C17.782 1.145 15.055 0 12 0 7.27 0 3.198 2.698 1.24 6.65l4.026 3.115Z" />
                <path fill="#34A853" d="M16.04 18.013c-1.09.703-2.474 1.078-4.04 1.078a7.077 7.077 0 0 1-6.723-4.823l-4.04 3.067A11.965 11.965 0 0 0 12 24c2.933 0 5.735-1.043 7.834-3l-3.793-2.987Z" />
                <path fill="#4A90E2" d="M19.834 21c2.195-2.048 3.62-5.096 3.62-9 0-.71-.109-1.473-.272-2.182H12v4.637h6.436c-.317 1.559-1.17 2.766-2.395 3.558L19.834 21Z" />
                <path fill="#FBBC05" d="M5.277 14.268A7.12 7.12 0 0 1 4.909 12c0-.782.125-1.533.357-2.235L1.24 6.65A11.934 11.934 0 0 0 0 12c0 1.92.445 3.73 1.237 5.335l4.04-3.067Z" />
              </svg>
              Google
            </button>
            <button
              type="button"
              onclick={() => handleSocialLogin("카카오")}
              class="w-full flex items-center justify-center px-4 py-2 border border-bright rounded-lg shadow-sm text-sm font-medium text-secondary bg-surface hover:bg-surface-hover transition-colors"
            >
              <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#FEE500">
                <path d="M12 3c5.8 0 10.5 3.66 10.5 8.185 0 4.525-4.7 8.185-10.5 8.185a13.5 13.5 0 0 1-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.867C1.5 6.66 6.2 3 12 3Z" />
              </svg>
              카카오
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<Toast />
